import { consoleRoutes } from './routes';

const decoded = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path;
}

function appendSuffix(base: string, suffix: string | undefined): string {
  return suffix ? `${base}/${suffix}` : base;
}

function normalizeOrigin(params: URLSearchParams): void {
  const from = params.get('from');
  if (!from) return;
  const [kind, tab] = from.split(':', 2);
  if (kind === 'sources-tab' && tab === 'sources') {
    params.set('from', 'sources-tab:citations');
    return;
  }
  if (kind !== 'site-tab') return;
  const canonicalTab: Record<string, string> = {
    pages: 'pages',
    coverage: 'coverage',
    crawlers: 'crawlers',
    visits: 'traffic',
    delivery: 'delivery',
  };
  params.set('from', `sources-tab:${canonicalTab[tab] ?? 'pages'}`);
}

function normalizeIssueView(params: URLSearchParams): void {
  // `evidence` was the old name for the issue's default view. Overview now has
  // the canonical bare detail URL; linked playbooks remains addressable.
  if (params.get('tab') === 'evidence') params.delete('tab');
}

const MODEL_SECTION_MAP: Record<string, string> = {
  facts: 'facts',
  products: 'products',
  competitors: 'competitors',
  capabilities: 'capabilities',
  features: 'capabilities',
  requirements: 'decision-criteria',
  committee: 'buyer-roles',
  personas: 'buyer-roles',
  themes: 'themes',
  review: 'discoveries',
  discovered: 'discoveries',
  journeys: 'journeys',
};

const COMPANY_RECORD_SECTIONS = new Set([
  'facts',
  'products',
  'capabilities',
  'decision-criteria',
  'buyer-roles',
  'competitors',
  'themes',
  'discoveries',
]);

function promptsRedirect(pathname: string, params: URLSearchParams): string {
  if (pathname === '/prompts') {
    const view = params.get('view');
    params.delete('view');
    return withQuery(
      view === 'fanout'
        ? consoleRoutes.data.queryFanOut
        : view === 'prompts'
          ? consoleRoutes.demand.questions
          : consoleRoutes.demand.topics,
      params,
    );
  }
  if (pathname === '/prompts/identity') {
    const section = params.get('section');
    params.delete('section');
    if (section === 'demand') return withQuery(consoleRoutes.demand.topics, params);
    const mapped = section ? MODEL_SECTION_MAP[section] : null;
    const target = mapped
      ? COMPANY_RECORD_SECTIONS.has(mapped)
        ? mapped === 'facts'
          ? consoleRoutes.knowledge.factLedger
          : consoleRoutes.knowledge.section(mapped)
        : consoleRoutes.knowledge.section(mapped)
      : consoleRoutes.knowledge.factLedger;
    return withQuery(target, params);
  }

  let match = pathname.match(/^\/prompts\/(?:groups|topics)\/([^/]+)(?:\/prompts)?$/);
  if (match) return withQuery(consoleRoutes.demand.topic(decoded(match[1])), params);

  match = pathname.match(/^\/prompts\/journeys\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.journey(decoded(match[1])), params);

  match = pathname.match(/^\/prompts\/competitors\/([^/]+)(?:\/(features|sources|profile))?$/);
  if (match) {
    return withQuery(appendSuffix(consoleRoutes.knowledge.competitor(decoded(match[1])), match[2]), params);
  }

  match = pathname.match(/^\/prompts\/features\/by-id\/([^/]+)(?:\/(sources|prompts))?$/);
  if (match) {
    const suffix = match[2] === 'prompts' ? 'questions' : match[2];
    return withQuery(appendSuffix(consoleRoutes.knowledge.capability(decoded(match[1])), suffix), params);
  }

  match = pathname.match(/^\/prompts\/features\/([^/]+)(?:\/(sources|prompts))?$/);
  if (match) {
    const suffix = match[2] === 'prompts' ? 'questions' : match[2];
    return withQuery(appendSuffix(consoleRoutes.knowledge.capabilityByName(decoded(match[1])), suffix), params);
  }

  match = pathname.match(/^\/prompts\/personas\/([^/]+)(?:\/(sources|prompts))?$/);
  if (match) {
    const suffix = match[2] === 'prompts' ? 'questions' : match[2];
    return withQuery(appendSuffix(consoleRoutes.knowledge.buyingRole(decoded(match[1])), suffix), params);
  }

  match = pathname.match(/^\/prompts\/products\/([^/]+)(?:\/(runs|features|competitors))?$/);
  if (match) {
    const suffix = match[2] === 'runs' ? 'answers' : match[2] === 'features' ? 'capabilities' : match[2];
    return withQuery(appendSuffix(consoleRoutes.knowledge.product(decoded(match[1])), suffix), params);
  }

  match = pathname.match(/^\/prompts\/keywords\/(.+)$/);
  if (match) return withQuery(consoleRoutes.demand.keyword(decoded(match[1])), params);

  match = pathname.match(/^\/prompts\/discovered\/([^/]+)(?:\/(sources))?$/);
  if (match) {
    return withQuery(appendSuffix(consoleRoutes.knowledge.reviewCandidate(decoded(match[1])), match[2]), params);
  }

  match = pathname.match(/^\/prompts\/themes\/([^/]+)(?:\/(sources))?$/);
  if (match) {
    return withQuery(appendSuffix(consoleRoutes.knowledge.theme(decoded(match[1])), match[2]), params);
  }

  match = pathname.match(/^\/prompts\/facts\/proposals\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.factProposal(decoded(match[1])), params);

  match = pathname.match(/^\/prompts\/facts\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.knowledge.fact(decoded(match[1]), decoded(match[2])), params);
  }

  return consoleRoutes.demand.topics;
}

function benchmarkModelRecordRedirect(pathname: string, params: URLSearchParams): string | null {
  let match = pathname.match(/^\/benchmarks\/model\/competitors\/([^/]+)(?:\/([^/]+))?$/);
  if (match) return withQuery(appendSuffix(consoleRoutes.knowledge.competitor(decoded(match[1])), match[2]), params);
  match = pathname.match(/^\/benchmarks\/model\/themes\/([^/]+)(?:\/([^/]+))?$/);
  if (match) return withQuery(appendSuffix(consoleRoutes.knowledge.theme(decoded(match[1])), match[2]), params);
  match = pathname.match(/^\/benchmarks\/model\/journeys\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.journey(decoded(match[1])), params);

  match = pathname.match(/^\/benchmarks\/model\/facts\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.knowledge.fact(decoded(match[1]), decoded(match[2])), params);
  }

  match = pathname.match(/^\/benchmarks\/model\/fact-proposals\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.factProposal(decoded(match[1])), params);

  match = pathname.match(/^\/benchmarks\/model\/capabilities\/by-name\/([^/]+)(?:\/([^/]+))?$/);
  if (match) {
    return withQuery(appendSuffix(consoleRoutes.knowledge.capabilityByName(decoded(match[1])), match[2]), params);
  }

  match = pathname.match(
    /^\/benchmarks\/model\/(products|capabilities|buying-committee|review-queue)\/([^/]+)(?:\/([^/]+))?$/,
  );
  if (match) {
    const section = match[1];
    const id = decoded(match[2]);
    const suffix = match[3];
    const base =
      section === 'products'
        ? consoleRoutes.knowledge.product(id)
        : section === 'capabilities'
          ? consoleRoutes.knowledge.capability(id)
          : section === 'buying-committee'
            ? consoleRoutes.knowledge.buyingRole(id)
            : consoleRoutes.knowledge.reviewCandidate(id);
    return withQuery(appendSuffix(base, suffix), params);
  }

  match = pathname.match(
    /^\/benchmarks\/model\/(facts|products|capabilities|requirements|buying-committee|review-queue)$/,
  );
  return match
    ? withQuery(
        match[1] === 'facts'
          ? consoleRoutes.knowledge.factLedger
          : consoleRoutes.knowledge.section(
              match[1] === 'requirements'
                ? 'decision-criteria'
                : match[1] === 'buying-committee'
                  ? 'buyer-roles'
                  : match[1],
            ),
        params,
      )
    : null;
}

function runsRedirect(pathname: string, params: URLSearchParams): string {
  if (pathname === '/runs' || pathname === '/runs/benchmarks') {
    const runsView = params.get('view') === 'runs';
    params.delete('view');
    return withQuery(runsView ? consoleRoutes.benchmarks.runs : consoleRoutes.benchmarks.results, params);
  }

  let match = pathname.match(/^\/runs\/reports\/([^/]+)\/edit$/);
  if (match) return withQuery(consoleRoutes.benchmarks.reportEdit(decoded(match[1])), params);

  match = pathname.match(/^\/runs\/eval\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.benchmarks.answer(decoded(match[1])), params);

  match = pathname.match(/^\/runs\/benchmarks\/([^/]+)\/changes\/([^/]+)\/(competitive|visibility)\/sources$/);
  if (match) {
    return withQuery(
      consoleRoutes.benchmarks.periodSourceChanges(
        decoded(match[1]),
        decoded(match[2]),
        match[3] as 'competitive' | 'visibility',
      ),
      params,
    );
  }

  match = pathname.match(/^\/runs\/benchmarks\/([^/]+)\/changes\/matrix\/(competitive|visibility)\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(
      consoleRoutes.benchmarks.periodTransitionChanges(
        decoded(match[1]),
        match[2] as 'competitive' | 'visibility',
        decoded(match[3]),
        decoded(match[4]),
      ),
      params,
    );
  }

  match = pathname.match(/^\/runs\/benchmarks\/([^/]+)\/pages\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.benchmarks.periodOwnedPage(decoded(match[1]), decoded(match[2])), params);
  }

  match = pathname.match(/^\/runs\/benchmarks\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.benchmarks.period(decoded(match[1])), params);

  match = pathname.match(/^\/runs\/coverage\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.benchmarks.coverage(decoded(match[1])), params);

  match = pathname.match(/^\/runs\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.question(decoded(match[1])), params);

  return consoleRoutes.benchmarks.results;
}

function siteRedirect(params: URLSearchParams): string {
  const tab = params.get('tab') ?? 'pages';
  const view = params.get('view');
  params.delete('tab');
  params.delete('view');
  const path =
    tab === 'coverage' || tab === 'beat'
      ? view === 'vocabulary'
        ? consoleRoutes.data.vocabulary
        : view === 'claims'
          ? consoleRoutes.data.answerClaims
          : consoleRoutes.publish.coverage
      : tab === 'crawlers' || tab === 'access' || tab === 'site'
        ? view === 'activity'
          ? consoleRoutes.traffic.agents
          : consoleRoutes.publish.access
        : tab === 'bots'
          ? consoleRoutes.traffic.agents
          : tab === 'visits'
            ? consoleRoutes.traffic.referrals
            : tab === 'delivery'
              ? consoleRoutes.publish.delivery
              : consoleRoutes.publish.pages;
  return withQuery(path, params);
}

/** Resolve a retired console URL without losing document viewer anchors. */
export function resolveLegacyConsoleLocation(pathname: string, search = '', hash = ''): string | null {
  const target = resolveLegacyConsolePath(pathname, search);
  return target === null ? null : `${target}${hash}`;
}

function resolveLegacyConsolePath(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search);
  normalizeOrigin(params);
  if (/^\/(?:issues|optimize\/issues|diagnosis\/issues)(?:\/|$)/.test(pathname)) normalizeIssueView(params);

  // A former Access-table link appended `&operator=` directly to a path with
  // no query string. Repair browser history and pasted addresses as well as
  // fixing the producer so the malformed path never strands a reader.
  let match = pathname.match(/^\/traffic\/agents&operator=([^/]+)$/);
  if (match) {
    params.set('operator', decoded(match[1]));
    return withQuery(consoleRoutes.traffic.agents, params);
  }

  if (pathname === '/home') return withQuery(consoleRoutes.overview, params);
  if (pathname === '/ai-visits') return withQuery(consoleRoutes.traffic.referrals, params);
  if (pathname === '/intelligence/authority') return withQuery(consoleRoutes.knowledge.authority, params);

  // Intelligence renamed two of its own addresses to the words the product uses
  // (Max, 2026-08-25): `Queries` is Query Fan Out, and the `Language` group is
  // Vocabulary. Canonical addresses must never redirect to themselves.
  if (pathname === '/intelligence/queries' || pathname === '/intelligence/query-fanout') {
    return withQuery(consoleRoutes.data.queryFanOut, params);
  }
  match = pathname.match(/^\/intelligence\/(?:queries|query-fanout)\/(.+)$/);
  if (match) return withQuery(consoleRoutes.data.queryFanOutGroup(decoded(match[1])), params);
  if (pathname === '/intelligence/vocabulary/terms' || pathname === '/intelligence/language/vocabulary') {
    return withQuery(consoleRoutes.data.vocabulary, params);
  }
  if (pathname === '/intelligence/language') return withQuery(consoleRoutes.data.vocabulary, params);
  if (pathname === '/intelligence/vocabulary/terms/pages' || pathname === '/intelligence/language/vocabulary/pages') {
    return withQuery(consoleRoutes.data.vocabularyPages, params);
  }
  if (pathname === '/intelligence/language/themes' || pathname === '/intelligence/vocabulary/themes') {
    return withQuery(consoleRoutes.data.themes, params);
  }

  // The previously canonical stable-job IA now resolves directly into the
  // lifecycle destinations selected by Option C.
  if (pathname === '/benchmarks/test-design' || pathname === '/benchmarks/test-design/topics') {
    return withQuery(consoleRoutes.demand.topics, params);
  }
  match = pathname.match(/^\/benchmarks\/test-design\/topics\/keywords\/(.+)$/);
  if (match) return withQuery(consoleRoutes.demand.keyword(decoded(match[1])), params);
  match = pathname.match(/^\/benchmarks\/test-design\/topics\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.topic(decoded(match[1])), params);
  if (pathname === '/benchmarks/test-design/questions') {
    return withQuery(consoleRoutes.demand.questions, params);
  }
  match = pathname.match(/^\/benchmarks\/test-design\/questions\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.question(decoded(match[1])), params);
  if (pathname === '/benchmarks/test-design/journeys') {
    return withQuery(consoleRoutes.knowledge.section('journeys'), params);
  }
  match = pathname.match(/^\/benchmarks\/test-design\/journeys\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.journey(decoded(match[1])), params);
  if (pathname === '/benchmarks/query-fanout') {
    return withQuery(consoleRoutes.data.queryFanOut, params);
  }

  if (pathname === '/fact-ledger' || pathname === '/fact-ledger/facts') {
    return withQuery(consoleRoutes.knowledge.factLedger, params);
  }
  match = pathname.match(/^\/fact-ledger\/facts\/([^/]+)\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.fact(decoded(match[1]), decoded(match[2])), params);
  match = pathname.match(/^\/fact-ledger\/fact-proposals\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.factProposal(decoded(match[1])), params);
  match = pathname.match(/^\/fact-ledger\/review-queue\/([^/]+)(?:\/([^/]+))?$/);
  if (match) {
    return withQuery(appendSuffix(consoleRoutes.knowledge.reviewCandidate(decoded(match[1])), match[2]), params);
  }
  match = pathname.match(/^\/fact-ledger\/journeys\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.knowledge.journey(decoded(match[1])), params);
  if (pathname === '/fact-ledger/review-queue') {
    return withQuery(consoleRoutes.knowledge.discoveries, params);
  }
  match = pathname.match(/^\/fact-ledger\/(capabilities|decision-criteria)\/by-name\/([^/]+)(?:\/([^/]+))?$/);
  if (match) {
    const base =
      match[1] === 'capabilities'
        ? consoleRoutes.knowledge.capabilityByName(decoded(match[2]))
        : consoleRoutes.knowledge.decisionCriterionByName(decoded(match[2]));
    return withQuery(appendSuffix(base, match[3]), params);
  }
  match = pathname.match(
    /^\/fact-ledger\/(products|capabilities|decision-criteria|buyer-roles|competitors|themes)(?:\/([^/]+))?(?:\/([^/]+))?$/,
  );
  if (match) {
    const [, section, rawId, suffix] = match;
    if (!rawId) return withQuery(consoleRoutes.knowledge.section(section), params);
    const id = decoded(rawId);
    const base =
      section === 'products'
        ? consoleRoutes.knowledge.product(id)
        : section === 'capabilities'
          ? consoleRoutes.knowledge.capability(id)
          : section === 'decision-criteria'
            ? consoleRoutes.knowledge.decisionCriterion(id)
            : section === 'buyer-roles'
              ? consoleRoutes.knowledge.buyingRole(id)
              : section === 'competitors'
                ? consoleRoutes.knowledge.competitor(id)
                : consoleRoutes.knowledge.theme(id);
    return withQuery(appendSuffix(base, suffix), params);
  }

  if (pathname === '/answer-evidence') return withQuery(consoleRoutes.data.sources, params);
  match = pathname.match(/^\/answer-evidence\/cited-sources\/domains\/(.+)$/);
  if (match) return withQuery(consoleRoutes.data.domain(decoded(match[1])), params);
  match = pathname.match(/^\/answer-evidence\/cited-sources\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.source(decoded(match[1])), params);
  if (pathname === '/answer-evidence/answer-claims') {
    return withQuery(consoleRoutes.data.answerClaims, params);
  }
  if (pathname === '/answer-evidence/answer-claims/topics') {
    return withQuery(consoleRoutes.data.answerClaimTopic, params);
  }
  if (pathname === '/answer-evidence/cited-themes') {
    return withQuery(consoleRoutes.data.themes, params);
  }
  if (pathname === '/issues') return withQuery(consoleRoutes.data.issues, params);
  match = pathname.match(/^\/issues\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.issue(decoded(match[1])), params);

  // Option C addresses were canonical briefly. Keep every one as a direct,
  // one-hop compatibility route into the approved lifecycle architecture.
  if (pathname === '/demand/query-fanout') return withQuery(consoleRoutes.data.queryFanOut, params);
  if (pathname === '/knowledge/sources') return withQuery(consoleRoutes.data.sources, params);
  if (pathname === '/data/coverage/content') return withQuery(consoleRoutes.publish.coverage, params);
  if (pathname === '/data/coverage/content/pages') return withQuery(consoleRoutes.publish.coveragePages, params);
  if (pathname === '/data') return withQuery(consoleRoutes.data.sources, params);
  if (pathname.startsWith('/data/')) {
    // The prefix swap can land on an address that was ITSELF renamed later
    // (`/data/queries` → `/intelligence/queries` → `/intelligence/query-fan-out`),
    // so resolve the result once more rather than handing back a URL that
    // immediately redirects again. Terminates: the swapped path is `/intelligence/…`.
    const swapped = pathname.replace(/^\/data/, consoleRoutes.data.root);
    return resolveLegacyConsolePath(swapped, params.toString()) ?? withQuery(swapped, params);
  }
  match = pathname.match(/^\/knowledge\/sources\/cited-sources\/domains\/(.+)$/);
  if (match) return withQuery(consoleRoutes.data.domain(decoded(match[1])), params);
  match = pathname.match(/^\/knowledge\/sources\/cited-sources\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.source(decoded(match[1])), params);

  if (pathname === '/knowledge') return withQuery(consoleRoutes.knowledge.factLedger, params);
  if (pathname.startsWith('/knowledge/fact-ledger')) {
    return withQuery(pathname.replace(/^\/knowledge\/fact-ledger/, consoleRoutes.knowledge.factLedger), params);
  }
  if (pathname.startsWith('/knowledge/discoveries')) {
    return withQuery(pathname.replace(/^\/knowledge\/discoveries/, consoleRoutes.knowledge.discoveries), params);
  }

  if (pathname === '/demand' || pathname === '/demand/topics') {
    return withQuery(consoleRoutes.demand.topics, params);
  }
  if (pathname === '/demand/questions') {
    return withQuery(consoleRoutes.demand.questions, params);
  }
  match = pathname.match(/^\/demand\/topics\/keywords\/(.+)$/);
  if (match) return withQuery(consoleRoutes.demand.keyword(decoded(match[1])), params);
  match = pathname.match(/^\/demand\/topics\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.topic(decoded(match[1])), params);
  match = pathname.match(/^\/demand\/questions\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.question(decoded(match[1])), params);

  if (pathname === '/optimize' || pathname === '/optimize/issues') {
    return withQuery(consoleRoutes.data.issues, params);
  }
  match = pathname.match(/^\/optimize\/issues\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.issue(decoded(match[1])), params);
  if (pathname === '/optimize/playbooks') return withQuery(consoleRoutes.publish.playbooks, params);
  match = pathname.match(/^\/optimize\/playbooks\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.playbook(decoded(match[1])), params);
  if (pathname === '/optimize/updates') return withQuery(consoleRoutes.publish.updates, params);
  if (pathname === '/optimize/updates/policy') return withQuery(consoleRoutes.publish.policy, params);
  match = pathname.match(/^\/optimize\/updates\/cycles\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.cycle(decoded(match[1])), params);

  if (pathname === '/publish') return withQuery(consoleRoutes.publish.pages, params);
  if (pathname.startsWith('/publish/')) {
    return withQuery(pathname.replace(/^\/publish/, consoleRoutes.publish.root), params);
  }

  if (pathname === '/benchmarks/runs') return withQuery(consoleRoutes.benchmarks.runs, params);
  if (pathname === '/benchmarks/reports') return withQuery(consoleRoutes.benchmarks.results, params);

  if (pathname === '/diagnosis' || pathname === '/diagnosis/issues') {
    return withQuery(consoleRoutes.data.issues, params);
  }
  match = pathname.match(/^\/diagnosis\/issues\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.issue(decoded(match[1])), params);
  if (pathname === '/diagnosis/answer-claims') return withQuery(consoleRoutes.data.answerClaims, params);
  if (pathname === '/diagnosis/answer-claims/topics') {
    return withQuery(consoleRoutes.data.answerClaimTopic, params);
  }
  if (pathname === '/diagnosis/cited-themes') return withQuery(consoleRoutes.data.themes, params);

  if (pathname === '/playbooks') return withQuery(consoleRoutes.publish.playbooks, params);
  match = pathname.match(/^\/playbooks\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.playbook(decoded(match[1])), params);

  if (pathname === '/site') return siteRedirect(params);
  if (pathname === '/site/pages') return withQuery(consoleRoutes.publish.pages, params);
  match = pathname.match(/^\/site\/pages\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.page(decoded(match[1])), params);
  if (pathname === '/site/coverage' || pathname === '/site/coverage/content') {
    return withQuery(consoleRoutes.publish.coverage, params);
  }
  if (pathname === '/site/coverage/content/pages') {
    return withQuery(consoleRoutes.publish.coveragePages, params);
  }
  if (pathname === '/site/coverage/pages') return withQuery(consoleRoutes.publish.coveragePages, params);
  if (pathname === '/site/coverage/vocabulary') return withQuery(consoleRoutes.data.vocabulary, params);
  if (pathname === '/site/coverage/vocabulary/pages') {
    return withQuery(consoleRoutes.data.vocabularyPages, params);
  }
  if (pathname === '/site/coverage/vocab') return withQuery(consoleRoutes.data.vocabularyPages, params);
  if (pathname === '/site/coverage/page-claims') return withQuery(consoleRoutes.data.pageClaims, params);
  if (pathname === '/site/access') return withQuery(consoleRoutes.publish.access, params);
  if (pathname === '/site/crawlers' || pathname === '/site/crawlers/access') {
    return withQuery(consoleRoutes.publish.access, params);
  }
  if (pathname === '/site/crawlers/activity') return withQuery(consoleRoutes.traffic.agents, params);
  if (pathname === '/site/traffic') return withQuery(consoleRoutes.traffic.referrals, params);
  match = pathname.match(/^\/site\/access\/checks\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.publish.healthCheck(decoded(match[1]), decoded(match[2])), params);
  }
  if (pathname === '/site/delivery') return withQuery(consoleRoutes.publish.delivery, params);
  if (pathname === '/site/updates') return withQuery(consoleRoutes.publish.updates, params);
  if (pathname === '/site/updates/policy') return withQuery(consoleRoutes.publish.policy, params);
  match = pathname.match(/^\/site\/updates\/cycles\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.cycle(decoded(match[1])), params);
  if (pathname === '/benchmarks/questions') {
    const view = params.get('view');
    params.delete('view');
    return withQuery(
      view === 'fanout'
        ? consoleRoutes.data.queryFanOut
        : view === 'prompts'
          ? consoleRoutes.demand.questions
          : consoleRoutes.demand.topics,
      params,
    );
  }
  match = pathname.match(/^\/benchmarks\/questions\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.question(decoded(match[1])), params);
  if (pathname === '/benchmarks/topics') {
    return withQuery(consoleRoutes.demand.topics, params);
  }
  match = pathname.match(/^\/benchmarks\/topics\/keywords\/(.+)$/);
  if (match) return withQuery(consoleRoutes.demand.keyword(decoded(match[1])), params);
  match = pathname.match(/^\/benchmarks\/topics\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.demand.topic(decoded(match[1])), params);
  if (pathname === '/benchmarks/model') return withQuery(consoleRoutes.knowledge.section('competitors'), params);
  if (pathname.startsWith('/benchmarks/model/')) {
    const companyRecordTarget = benchmarkModelRecordRedirect(pathname, params);
    if (companyRecordTarget) return companyRecordTarget;
  }
  if (pathname.startsWith('/prompts')) return promptsRedirect(pathname, params);
  if (pathname.startsWith('/runs')) return runsRedirect(pathname, params);

  if (pathname === '/company-record' || pathname === '/company-record/facts') {
    return withQuery(consoleRoutes.knowledge.factLedger, params);
  }
  if (pathname.startsWith('/company-record/')) {
    const migrated = pathname
      .replace(/^\/company-record\/requirements(?=\/|$)/, '/facts/fact-ledger/decision-criteria')
      .replace(/^\/company-record\/buying-committee(?=\/|$)/, '/facts/fact-ledger/buyer-roles')
      .replace(/^\/company-record\/review-queue(?=\/|$)/, '/facts/discoveries')
      .replace(/^\/company-record/, '/facts/fact-ledger');
    return withQuery(migrated, params);
  }

  if (pathname === '/settings/general') return withQuery(consoleRoutes.settings.workspace, params);
  if (pathname === '/settings/api' || pathname === '/settings/api-keys') {
    return withQuery(consoleRoutes.settings.mcp, params);
  }

  if (pathname === '/optimization' || pathname === '/optimization/queue') {
    return withQuery(consoleRoutes.publish.updates, params);
  }
  if (pathname === '/optimization/policy') {
    return withQuery(consoleRoutes.publish.policy, params);
  }
  match = pathname.match(/^\/optimization\/cycles\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.cycle(decoded(match[1])), params);

  if (pathname === '/content') {
    const tab = params.get('tab');
    if (!tab) return null;
    params.delete('tab');
    const target =
      tab === 'coverage'
        ? consoleRoutes.publish.coverage
        : tab === 'vocabulary'
          ? consoleRoutes.data.vocabulary
          : tab === 'claims'
            ? consoleRoutes.data.answerClaims
            : consoleRoutes.publish.pages;
    return withQuery(target, params);
  }
  match = pathname.match(/^\/content\/health\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.publish.healthCheck(decoded(match[1]), decoded(match[2])), params);
  }
  if (pathname === '/content/coverage') return null;
  if (pathname === '/content/coverage/content') {
    return withQuery(consoleRoutes.publish.coverage, params);
  }
  if (pathname === '/content/coverage/pages') return null;
  if (pathname === '/content/coverage/content/pages') {
    return withQuery(consoleRoutes.publish.coveragePages, params);
  }
  if (pathname === '/content/coverage/vocabulary') return withQuery(consoleRoutes.data.vocabulary, params);
  if (pathname === '/content/coverage/vocabulary/pages' || pathname === '/content/coverage/vocab') {
    return withQuery(consoleRoutes.data.vocabularyPages, params);
  }
  if (pathname === '/content/coverage/claims') return withQuery(consoleRoutes.data.answerClaims, params);
  // `/content/pages`, `/content/access`, and `/content/delivery` are current
  // canonical addresses. Their explicit App routes handle them, and this
  // compatibility resolver must never point a canonical URL back to itself.
  if (
    new Set([
      '/content/playbooks',
      '/content/updates',
      '/content/agent-ledger',
      '/content/pages',
      '/content/interfaces',
      '/content/access',
      '/content/delivery',
    ]).has(pathname)
  ) {
    return null;
  }
  match = pathname.match(/^\/content\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.publish.page(decoded(match[1])), params);

  if (pathname === '/mirror') {
    const delivery = params.get('tab') === 'delivery';
    params.delete('tab');
    return withQuery(delivery ? consoleRoutes.publish.delivery : consoleRoutes.publish.pages, params);
  }
  match = pathname.match(/^\/mirror\/([^/]+)$/);
  if (match) {
    params.set('tab', 'agent');
    return withQuery(consoleRoutes.publish.page(decoded(match[1])), params);
  }

  match = pathname.match(/^\/sources\/by-domain\/(.+)$/);
  if (match) return withQuery(consoleRoutes.data.domain(decoded(match[1])), params);
  match = pathname.match(/^\/sources\/health\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.publish.healthCheck(decoded(match[1]), decoded(match[2])), params);
  }
  if (pathname === '/sources/pages') {
    return withQuery(consoleRoutes.publish.pages, params);
  }
  if (pathname === '/sources/coverage' || pathname === '/sources/coverage/content') {
    return withQuery(consoleRoutes.publish.coverage, params);
  }
  if (pathname === '/sources/coverage/content/pages') {
    return withQuery(consoleRoutes.publish.coveragePages, params);
  }
  if (pathname === '/sources/coverage/vocabulary') {
    return withQuery(consoleRoutes.data.vocabulary, params);
  }
  if (pathname === '/sources/coverage/vocabulary/pages') {
    return withQuery(consoleRoutes.data.vocabularyPages, params);
  }
  if (pathname === '/sources/coverage/claims') {
    return withQuery(consoleRoutes.data.answerClaims, params);
  }
  if (pathname === '/sources/coverage/claims/topics') {
    return withQuery(consoleRoutes.data.answerClaimTopic, params);
  }
  if (pathname === '/sources/crawlers' || pathname === '/sources/crawlers/access') {
    return withQuery(consoleRoutes.publish.access, params);
  }
  if (pathname === '/sources/crawlers/activity') {
    return withQuery(consoleRoutes.traffic.agents, params);
  }
  match = pathname.match(/^\/sources\/crawlers\/checks\/([^/]+)\/([^/]+)$/);
  if (match) {
    return withQuery(consoleRoutes.publish.healthCheck(decoded(match[1]), decoded(match[2])), params);
  }
  if (pathname === '/sources/traffic') {
    return withQuery(consoleRoutes.traffic.referrals, params);
  }
  if (pathname === '/sources/delivery') {
    return withQuery(consoleRoutes.publish.delivery, params);
  }
  if (pathname === '/sources/coverage/pages') {
    return withQuery(consoleRoutes.publish.coveragePages, params);
  }
  if (pathname === '/sources/coverage/vocab') {
    return withQuery(consoleRoutes.data.vocabularyPages, params);
  }
  if (pathname === '/sources' || pathname === '/sources/citations') {
    return withQuery(consoleRoutes.data.sources, params);
  }
  match = pathname.match(/^\/sources\/records\/([^/]+)$/);
  if (match) return withQuery(consoleRoutes.data.source(decoded(match[1])), params);
  match = pathname.match(/^\/sources\/([^/]+)$/);
  if (match && match[1] !== 'citations') {
    return withQuery(consoleRoutes.data.source(decoded(match[1])), params);
  }

  if (pathname === '/site/coverage/claims') {
    return withQuery(consoleRoutes.data.answerClaims, params);
  }
  if (pathname === '/site/coverage/claims/topics') {
    return withQuery(consoleRoutes.data.answerClaimTopic, params);
  }

  return null;
}
